/**
 * Step-up authentication (T22) — the queue between the axios interceptor and
 * the one StepUpModal.
 *
 * Binding money actions (decide / settle) answer 401 `step_up_required` when
 * the session's last real sign-in is older than 5 minutes. The shared client
 * (@cardioq/api-client) calls `requestStepUp()`; this module opens the modal
 * once, and every 401 that lands while it is open waits on the SAME prompt
 * (one code, one new token, every queued request retried with it).
 *
 * Deliberately React-free so main.tsx can wire it at boot without pulling the
 * lazy insurance bundle in, and so the queue logic is unit-testable.
 */

import { getApiClient, type TokenStore } from '@cardioq/api-client';

/** Rejection the original request receives when the user cancels. */
export class StepUpCancelledError extends Error {
  readonly code = 'step_up_cancelled';
  constructor(message = 'Verification cancelled — the action was not taken.') {
    super(message);
    this.name = 'StepUpCancelledError';
  }
}

/** What the modal is handed while open: finish with a token, or cancel. */
export interface StepUpPrompt {
  resolve: (accessToken: string) => void;
  reject: (err: Error) => void;
}

type Opener = (prompt: StepUpPrompt) => void;

let opener: Opener | null = null;
let inFlight: Promise<string> | null = null;

/** The mounted StepUpModal registers here; returns the unregister function. */
export function registerStepUpOpener(fn: Opener): () => void {
  opener = fn;
  return () => {
    if (opener === fn) opener = null;
  };
}

/**
 * Ask the user to step up. Concurrent callers share one prompt; once it
 * settles, the next call opens a fresh one. Rejects at once when no modal is
 * mounted (a page outside the insurance section) rather than hanging.
 */
export function requestStepUp(): Promise<string> {
  if (inFlight) return inFlight;
  const open = opener;
  if (!open) {
    return Promise.reject(
      new StepUpCancelledError('Please verify your identity again to complete this action.'),
    );
  }
  inFlight = new Promise<string>((resolve, reject) => {
    open({ resolve, reject });
  }).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Test seam — forget any registered modal and pending prompt. */
export function resetStepUpForTests(): void {
  opener = null;
  inFlight = null;
}

// ------------------------------------------------------------ API calls

export interface StepUpSendResult {
  sent: boolean;
  expires_in_seconds: number;
  destination: string;
}

export interface StepUpVerifyResult {
  access_token: string;
  token_type: string;
  expires_in_seconds: number;
  auth_time: number;
}

export const stepUpApi = {
  send: () => getApiClient().post<StepUpSendResult>('/auth/step-up/send').then((r) => r.data),
  verify: (otp: string) =>
    getApiClient()
      .post<StepUpVerifyResult>('/auth/step-up/verify', { otp })
      .then((r) => r.data),
};

/**
 * Store a step-up token the way login stores tokens. The step-up response
 * carries only an access token; the session's refresh token is kept (the
 * patient store's setTokens would otherwise null it).
 */
export function storeStepUpToken(store: TokenStore, accessToken: string): void {
  store.setTokens(accessToken, store.getRefreshToken() ?? undefined);
}
